import { addDays, format } from "date-fns";
import type {
  CameraTask,
  TaskStatus,
  TaskTemplate,
} from "@/domains/tasks/api/types";
import { SEED_TASKS } from "@/domains/tasks/mocks/tasks";
import { SEED_TASK_TEMPLATES } from "@/domains/tasks/mocks/taskTemplates";

let tasks: Array<CameraTask> = SEED_TASKS.map((t) => ({ ...t }));
let templates: Array<TaskTemplate> = SEED_TASK_TEMPLATES.map((t) => ({
  ...t,
  items: t.items.map((i) => ({ ...i })),
}));
let nextTaskCounter = 100;
let nextTemplateCounter = 100;

function delay<T>(value: T, ms = 200): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

export async function fetchTasks(): Promise<ReadonlyArray<CameraTask>> {
  return delay(tasks);
}

export async function fetchTasksByCase(
  caseId: string,
): Promise<ReadonlyArray<CameraTask>> {
  return delay(tasks.filter((t) => t.caseId === caseId));
}

export async function fetchTasksByAssignee(
  userId: string,
): Promise<ReadonlyArray<CameraTask>> {
  return delay(tasks.filter((t) => t.assignee?.userId === userId));
}

export async function fetchTaskTemplates(): Promise<ReadonlyArray<TaskTemplate>> {
  return delay(templates);
}

export async function updateTaskStatus(
  id: string,
  status: TaskStatus,
): Promise<CameraTask> {
  const idx = tasks.findIndex((t) => t.id === id);
  if (idx === -1) throw new Error(`Taak niet gevonden: ${id}`);
  const updated: CameraTask = { ...tasks[idx], status };
  tasks = [...tasks.slice(0, idx), updated, ...tasks.slice(idx + 1)];
  return delay(updated, 150);
}

export async function updateTaskDueDate(
  id: string,
  dueDate: string,
): Promise<CameraTask> {
  const idx = tasks.findIndex((t) => t.id === id);
  if (idx === -1) throw new Error(`Taak niet gevonden: ${id}`);
  const updated: CameraTask = { ...tasks[idx], dueDate };
  tasks = [...tasks.slice(0, idx), updated, ...tasks.slice(idx + 1)];
  return delay(updated, 100);
}

export async function generateTasksFromTemplate(
  caseId: string,
  templateId: string,
  startDate: string,
  defaultAssignee: { userId: string; name: string },
  partnerAssignee: { userId: string; name: string },
): Promise<ReadonlyArray<CameraTask>> {
  const template = templates.find((t) => t.id === templateId);
  if (!template) throw new Error(`Template niet gevonden: ${templateId}`);
  const start = new Date(startDate);
  const created: Array<CameraTask> = template.items.map((item) => {
    nextTaskCounter += 1;
    const due = format(addDays(start, item.offsetDays), "yyyy-MM-dd");
    const assigneeMatch =
      item.assigneeKind === "partner" ? partnerAssignee : defaultAssignee;
    return {
      id: `tsk-${caseId}-${nextTaskCounter}`,
      caseId,
      title: item.title,
      description: item.description,
      category: item.category,
      status: "todo",
      assignee: {
        kind: item.assigneeKind,
        userId: assigneeMatch.userId,
        name: assigneeMatch.name,
      },
      dueDate: due,
      templateId,
    };
  });
  tasks = [...tasks, ...created];
  return delay(created, 250);
}

export async function addCustomTask(
  task: Omit<CameraTask, "id">,
): Promise<CameraTask> {
  nextTaskCounter += 1;
  const created: CameraTask = { ...task, id: `tsk-${task.caseId}-${nextTaskCounter}` };
  tasks = [...tasks, created];
  return delay(created, 150);
}

export async function updateTask(
  id: string,
  patch: Partial<Omit<CameraTask, "id" | "caseId">>,
): Promise<CameraTask> {
  const idx = tasks.findIndex((t) => t.id === id);
  if (idx === -1) throw new Error(`Taak niet gevonden: ${id}`);
  const updated: CameraTask = { ...tasks[idx], ...patch };
  tasks = [...tasks.slice(0, idx), updated, ...tasks.slice(idx + 1)];
  return delay(updated, 120);
}

export async function deleteTask(id: string): Promise<void> {
  tasks = tasks.filter((t) => t.id !== id && t.parentId !== id);
  return delay(undefined, 100);
}

export async function saveTaskTemplate(template: TaskTemplate): Promise<TaskTemplate> {
  const idx = templates.findIndex((t) => t.id === template.id);
  if (idx === -1) {
    templates = [...templates, template];
  } else {
    templates = [...templates.slice(0, idx), template, ...templates.slice(idx + 1)];
  }
  return delay(template, 120);
}

export async function createTaskTemplate(
  input: Omit<TaskTemplate, "id">,
): Promise<TaskTemplate> {
  nextTemplateCounter += 1;
  const created: TaskTemplate = {
    ...input,
    id: `tpl-task-${nextTemplateCounter}`,
    items: input.items.map((i) => ({ ...i })),
  };
  templates = [...templates, created];
  return delay(created, 150);
}

export async function deleteTaskTemplate(id: string): Promise<void> {
  templates = templates.filter((t) => t.id !== id);
  return delay(undefined, 100);
}
